import React, { useMemo, useState } from 'react';
import FormRenderer from '../workflows/FormRenderer';
import { WorkflowCatalogItem, WorkflowDetails } from '../../types/workflow';
import { useStudioContext, SheetSelection } from '../../contexts/StudioContext';
import { getApiBaseUrl } from '../../config/environment';

const API_BASE = getApiBaseUrl();

/** Route any URL through the server-side image proxy to avoid Google Drive auth restrictions. */
const proxyImageUrl = (url: string) =>
  `${API_BASE}/api/image-proxy?url=${encodeURIComponent(url)}`;

interface WorkflowPanelProps {
  workflows: WorkflowCatalogItem[];
  selectedWorkflow: WorkflowDetails | null;
  onSelectWorkflow: (workflowId: string, version: string) => void;
  onSubmit: (inputs: Record<string, any>) => void;
  loading: boolean;
  disabled: boolean;
}

const WorkflowPanel: React.FC<WorkflowPanelProps> = ({
  workflows,
  selectedWorkflow,
  onSelectWorkflow,
  onSubmit,
  loading,
  disabled
}) => {
  const [showWorkflowSelector, setShowWorkflowSelector] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { context, removeProduct, setSheetSelection } = useStudioContext();
  const workflowContextInputs = useMemo(
    () => (selectedWorkflow?.manifest.inputs || []).filter(input => input.source === 'context'),
    [selectedWorkflow]
  );
  const supportsProductContext = workflowContextInputs.some(input => input.type === 'product-list');
  const selectedProducts = supportsProductContext ? context.selectedProducts : [];

  // Active sheet-row selections, paired with their workflow-declared section
  // title (falls back to contextId/key) so the Active Context block can label
  // them the same way the sidebar does.
  const activeSheetSelections = useMemo<Array<{ selection: SheetSelection; title: string }>>(() => {
    return Object.values(context.sheetSelections)
      .filter((sel): sel is SheetSelection => !!sel)
      .map(sel => {
        const input = workflowContextInputs.find(
          i => i.type === 'sheet-row' && (i.context_id === sel.contextId || i.key === sel.contextId)
        );
        return input ? { selection: sel, title: input.title || sel.contextId } : null;
      })
      .filter((item): item is { selection: SheetSelection; title: string } => !!item)
      .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
  }, [context.sheetSelections, workflowContextInputs]);

  const totalContextItems = selectedProducts.length + activeSheetSelections.length;
  const hasActiveContext  = totalContextItems > 0;

  const handleSubmit = (inputs: Record<string, any>) => {
    onSubmit(inputs);
  };

  // ── Collapsed icon rail ─────────────────────────────────────────────────────
  // Mirrors the ContextPanel collapse pattern, anchored to the right edge.
  // Surfaces only summary indicators — workflow icon plus an active-context
  // badge — so the user keeps a sense of state without occupying canvas space.
  if (collapsed) {
    const workflowIcon = selectedWorkflow?.manifest.icon;
    const isImageIcon = !!workflowIcon && (
      workflowIcon.startsWith('http://') ||
      workflowIcon.startsWith('https://') ||
      workflowIcon.startsWith('data:')
    );
    return (
      <div className="w-12 h-full max-lg:w-full bg-surface-panel border-l max-lg:border-l-0 max-lg:border-t border-gray-200 flex flex-col max-lg:flex-row items-center pt-3 max-lg:pt-0 max-lg:px-3 gap-5 max-lg:gap-4 overflow-x-auto scrollbar-touch transition-[width] duration-base">
        {/* Expand button — chevron points back toward the canvas centre */}
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-fast touch-target flex items-center justify-center"
          title="Expand workflow panel"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Workflow icon — emoji or URL — when a workflow is selected */}
        {selectedWorkflow && (
          <div
            className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-base overflow-hidden"
            title={selectedWorkflow.manifest.displayName || selectedWorkflow.manifest.name}
          >
            {isImageIcon ? (
              <img src={workflowIcon} alt="" className="w-full h-full object-cover" />
            ) : (
              <span>{workflowIcon || '🎨'}</span>
            )}
          </div>
        )}

        {/* Active-context badge — shows the same total as the expanded panel */}
        {totalContextItems > 0 && (
          <div className="relative" title={`Active context (${totalContextItems})`}>
            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-surface-panel">
              {totalContextItems}
            </span>
          </div>
        )}

        {/* Running indicator — subtle pulse while a job is in flight */}
        {disabled && (
          <div className="mt-1" title="Generating…">
            <svg className="animate-spin w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}
      </div>
    );
  }

  const hasMultipleWorkflows = workflows.length > 1;

  return (
    <div className="w-96 max-lg:w-full h-full bg-surface-panel border-l max-lg:border-l-0 max-lg:border-t border-gray-200 flex flex-col overflow-hidden shadow-xs">
      {/* Panel Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        {/* Collapse chevron + section eyebrow */}
        <div className="flex items-center justify-between mb-3 gap-3">
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="text-gray-500 hover:text-gray-700 p-1 transition-colors duration-fast flex-shrink-0 touch-target flex items-center justify-center"
            title="Collapse panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Create</p>
        </div>

        {/* Workflow info + selector */}
        <div className="space-y-1.5">
          {/* 1. Workflow title - prominent */}
          {(() => {
            // Use selectedWorkflow if available, otherwise fall back to first workflow in list
            const displayName = selectedWorkflow?.manifest.displayName?.trim()
              || (workflows.length > 0 ? (workflows[0] as any).displayName?.trim() : null)
              || (workflows.length > 0 ? 'Loading workflow…' : 'No workflows available');
            return (
              <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                {displayName}
              </h2>
            );
          })()}

          {/* 2. Workflow description - muted */}
          {(() => {
            const description = selectedWorkflow?.manifest.description
              || (workflows.length > 0 ? (workflows[0] as any).description : null);
            return description ? (
              <p className="text-sm text-gray-500 leading-snug">
                {description}
              </p>
            ) : null;
          })()}

          {/* 3. Change workflow link - only when multiple workflows available */}
          {hasMultipleWorkflows && (
            <button
              type="button"
              onClick={() => setShowWorkflowSelector(!showWorkflowSelector)}
              aria-expanded={showWorkflowSelector}
              aria-haspopup="listbox"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-fast pt-1"
            >
              Change workflow
              <svg
                className={`w-3.5 h-3.5 transition-transform duration-fast ${showWorkflowSelector ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Workflow Selector Dropdown */}
      {showWorkflowSelector && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-touch">
            {workflows.map((workflow: any) => {
              // Support both old and new API structures
              const workflowId = workflow.rowKey || workflow.workflowId;
              const version = workflow.version || workflow.latestVersion || '1.0.0';
              const isSelected = workflowId === selectedWorkflow?.workflowId;
              return (
                <button
                  type="button"
                  key={workflowId}
                  onClick={() => {
                    onSelectWorkflow(workflowId, version);
                    setShowWorkflowSelector(false);
                  }}
                  className={`w-full p-3 rounded-lg border-2 transition-all duration-base touch-target ${
                    isSelected
                      ? 'bg-blue-50 border-blue-500 shadow-focus'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3 w-full min-w-0">
                    <div className="flex-1 min-w-0 text-left">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {workflow.displayName?.trim() || 'Untitled workflow'}
                      </h4>
                      <p className="text-xs text-gray-500 truncate">
                        {workflow.description}
                      </p>
                    </div>
                    {isSelected && (
                      <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 scrollbar-touch">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading workflow...</p>
            </div>
          </div>
        ) : selectedWorkflow ? (
          <div className="space-y-6">
            {/* Form Renderer */}
            <FormRenderer
              schema={selectedWorkflow.uiConfig.jsonSchema}
              uiSchema={selectedWorkflow.uiConfig.uiSchema}
              onSubmit={handleSubmit}
              disabled={disabled}
            />

          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm text-gray-500">No workflow selected</p>
            </div>
          </div>
        )}
      </div>

      {/* Context Indicator — shows products + sheet-row selections that will be applied */}
      {hasActiveContext && selectedWorkflow && !loading && (
        <div className="px-4 py-3 border-t border-gray-200 bg-blue-50">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
            Active context ({totalContextItems})
          </p>
          <div className="space-y-1.5 max-h-40 overflow-y-auto scrollbar-touch">
            {/* Sheet-row selections (Locations, Lighting, …) */}
            {activeSheetSelections.map(({ selection, title }) => {
              const thumb = selection.row?.Thumbnail || selection.row?.thumbnail || '';
              return (
                <div
                  key={selection.contextId}
                  className="flex items-center gap-2 bg-white border border-purple-200 rounded-md px-2 py-1"
                >
                  {thumb && (
                    <img
                      src={proxyImageUrl(thumb)}
                      alt={selection.rowTitle}
                      className="w-7 h-7 rounded object-cover flex-shrink-0 bg-gray-100"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                  <span className="flex-1 text-xs text-gray-700 truncate">{selection.rowTitle}</span>
                  <button
                    type="button"
                    onClick={() => setSheetSelection(selection.contextId, null)}
                    className="text-gray-400 hover:text-red-500 flex-shrink-0 touch-target flex items-center justify-center"
                    title={`Clear ${title}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              );
            })}

            {/* Product selections */}
            {selectedProducts.map(product => (
              <div
                key={product.productId}
                className="flex items-center gap-2 bg-white border border-blue-200 rounded-md px-2 py-1"
              >
                {product.primaryImage && (
                  <img
                    src={product.primaryImage}
                    alt={product.name}
                    className="w-7 h-7 rounded object-cover flex-shrink-0 bg-gray-100"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}
                <span className="flex-1 text-xs text-gray-700 truncate">{product.name}</span>
                <button
                  type="button"
                  onClick={() => removeProduct(product.productId)}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0 touch-target flex items-center justify-center"
                  title="Remove from context"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Button - Fixed at bottom. Primary CTA: larger type, generate
          shadow + ring, and a hover lift to make it the clear action target. */}
      {selectedWorkflow && !loading && (
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-gray-200 bg-surface-panel safe-b">
          <button
            type="submit"
            form="workflow-form"
            disabled={disabled}
            className={`w-full py-4 px-4 rounded-lg font-bold text-lg tracking-widest uppercase text-white text-center touch-target
              transition-all duration-base flex items-center justify-center gap-2.5
              ${disabled
                ? 'bg-gray-300 cursor-not-allowed text-gray-400'
                : 'bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 shadow-generate hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md ring-1 ring-blue-700/20'
              }`}
          >
            {disabled ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Creating…</span>
              </>
            ) : <span className="w-full text-center">Create</span>}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkflowPanel;

